import { makeAutoObservable, runInAction } from "mobx";
import { IS_DEV } from "@/utils/env";
import { getHardwareStatus } from "@/utils/hardwareDetection";

const SESSION_PREFER_REDUCED_MOTION_KEY = "preferReducedMotion";

/** @returns {boolean} */
function readSessionPreferReducedMotion() {
  if (typeof sessionStorage === "undefined") return false;
  try {
    return sessionStorage.getItem(SESSION_PREFER_REDUCED_MOTION_KEY) === "1";
  } catch {
    return false;
  }
}

/** @param {boolean} enabled */
function writeSessionPreferReducedMotion(enabled) {
  if (typeof sessionStorage === "undefined") return;
  try {
    if (enabled) sessionStorage.setItem(SESSION_PREFER_REDUCED_MOTION_KEY, "1");
    else sessionStorage.removeItem(SESSION_PREFER_REDUCED_MOTION_KEY);
  } catch {
    // ignore quota / private mode
  }
}

/** @type {(mql: MediaQueryList, fn: () => void) => void} */
const subscribeMotionPreference = (mq, fn) => {
  if (typeof mq.addEventListener === "function") {
    mq.addEventListener("change", fn);
  } else {
    mq.addListener(fn);
  }
};

/**
 * Browser / device capability flags used for performance tiering (MUI theme, motion-heavy UI).
 */
class SystemStore {
  /** Low-end profiling from {@link getHardwareStatus} (cached globally). */
  isLowEnd = false;
  /** Filled when profiling completes when {@link SystemStore.isLowEnd} matters for diagnostics. */
  hardwareReason = "";
  prefersReducedMotion = false;
  /** Session-only override: treat the SPA as preferring reduced motion (see Settings). */
  sessionPreferReducedMotion = false;

  constructor() {
    makeAutoObservable(this);
    if (typeof window === "undefined") return;

    const self = this;

    this.sessionPreferReducedMotion = readSessionPreferReducedMotion();

    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.prefersReducedMotion = mq.matches;
    subscribeMotionPreference(mq, () => {
      runInAction(() => {
        this.prefersReducedMotion = mq.matches;
      });
    });

    void getHardwareStatus().then((status) => {
      runInAction(() => {
        this.isLowEnd = status.isLowEnd;
        this.hardwareReason = status.reason;
      });
    });

    if (IS_DEV) {
      window.__haiHardwareTier = {
        get isLowEnd() {
          return self.isLowEnd;
        },
        get hardwareReason() {
          return self.hardwareReason;
        },
      };
    }
  }

  /**
   * When true, prefer fewer transitions and cheaper compositing across the UI.
   * @returns {boolean}
   */
  get isPerformanceModeActive() {
    return this.isLowEnd || this.prefersReducedMotion || this.sessionPreferReducedMotion;
  }

  /**
   * Persisted for the browser tab only; combined with OS reduced-motion for theme / motion tiering.
   * @param {boolean} enabled
   */
  setSessionPreferReducedMotion(enabled) {
    const next = Boolean(enabled);
    runInAction(() => {
      this.sessionPreferReducedMotion = next;
    });
    writeSessionPreferReducedMotion(next);
  }
}

export const systemStore = new SystemStore();
